import React from 'react';
import type { Lang } from '@/types';
import { ic, sized } from '@/components/Icons';
import { useI18n } from '@/i18n';
import { useAppStore } from '@/store/useAppStore';
import { useGo } from '@/hooks/useRoute';
import { Logo } from './Logo';
import { NotifBell } from './NotifBell';
import { SecurityModal } from './SecurityModal';

export function Topbar({ onLogout }: { onLogout: () => void }) {
  const { lang, t, setLang } = useI18n();
  const go = useGo();
  const user = useAppStore((s) => s.user);
  const role = useAppStore((s) => s.role);
  const windowState = useAppStore((s) => s.windowState);

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [seguridadOpen, setSeguridadOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const statusKey = windowState.open ? 'window.status_open' : 'window.status_closed';

  return (
    <header
      className="topbar"
      style={{
        height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', background: 'var(--surface)', borderBottom: '1px solid var(--line)',
        position: 'sticky', top: 0, zIndex: 20,
      }}
    >
      <Logo size={20} onClick={() => go('dashboard')} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* update-window status chip */}
        <span className={'chip m-hide ' + (windowState.open ? 'ok' : 'lock')} data-tip-pos="below" data-tip={t(statusKey)}>
          <span style={{ width: 6, height: 6, borderRadius: 99, background: 'currentColor', display: 'inline-block' }} />
          {t(statusKey)}
        </span>

        {/* language toggle */}
        <div role="group" aria-label="language" style={{ display: 'flex', background: 'var(--bg)', borderRadius: 7, padding: 2, border: '1px solid var(--line)' }}>
          {(['es', 'en'] as Lang[]).map((l) => (
            <button
              key={l}
              className="btn sm"
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              style={{
                background: lang === l ? 'var(--surface)' : 'transparent',
                border: 'none',
                boxShadow: lang === l ? 'var(--shadow-1)' : 'none',
                color: lang === l ? 'var(--brand-ink)' : 'var(--text-2)',
                fontWeight: 700, fontSize: 11, padding: '4px 8px',
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <NotifBell />

        {/* user menu */}
        <div ref={ref} style={{ position: 'relative' }}>
          <button className="btn ghost" onClick={() => setMenuOpen((o) => !o)} style={{ gap: 10, paddingRight: 8 }}>
            <span
              style={{
                width: 28, height: 28, borderRadius: 99, background: 'var(--brand-primary-l)',
                color: 'var(--brand-primary-d)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 12,
              }}
            >
              {(user?.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
            </span>
            <span className="m-hide" style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{user?.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginTop: 2 }}>{t('roles.' + role)}</span>
            </span>
            {ic.chevD}
          </button>

          {menuOpen ? (
            <div
              style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10,
                boxShadow: 'var(--shadow-3)', padding: 10, minWidth: 260, zIndex: 50,
              }}
            >
              {/* Quién eres. El rol lo fija la cuenta; aquí solo se lee.
                  Antes había un selector para cambiarlo, ayuda de la maqueta
                  para recorrer las tres vistas sin tener tres cuentas. */}
              <div style={{ padding: '6px 8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>
                  {t('roles.' + role)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3 }}>
                  {user?.username}
                </div>
              </div>
              <div style={{ height: 1, background: 'var(--line)', margin: '8px 4px' }} />
              <button
                className="btn ghost"
                onClick={() => { setMenuOpen(false); setSeguridadOpen(true); }}
                style={{ width: '100%', justifyContent: 'flex-start' }}
              >
                {sized(ic.lock, 15)}
                {lang === 'es' ? 'Verificación en dos pasos' : 'Two-step verification'}
              </button>
              <button className="btn ghost" onClick={onLogout} style={{ width: '100%', justifyContent: 'flex-start' }}>
                {t('common.signout')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <SecurityModal open={seguridadOpen} onClose={() => setSeguridadOpen(false)} />
    </header>
  );
}
